import { useCallback, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import GitHubIcon from '@mui/icons-material/GitHub';
import HubIcon from '@mui/icons-material/Hub';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import {
  integrationService,
  type ConnectionTarget,
  type IntegrationConnection,
} from '@services/integrationService';
import { PROVIDERS } from './providers';

interface Props {
  orgId: string;
  connections: IntegrationConnection[];
  onConnect: (providerId: string) => void;
  onDisconnect: (connectionId: string) => void;
  deletingId: string | null;
}

/** Per-connection drill-in state. Kept keyed so several can be open at once. */
interface TargetsState {
  loading: boolean;
  error: boolean;
  targets: ConnectionTarget[] | null;
}

/**
 * REV-492: the Integrations provider catalog.
 *
 * Integrations was a hardcoded GitHub form plus a flat list, which answered
 * "what have I connected?" but never "what CAN I connect?" — a user could not
 * discover Jira existed. This shows every provider as a card with its connected
 * state, and drills into a connected one to reveal its connections and the
 * tracked places routed through each.
 *
 * That drill-in is the useful part: REV-514 made disconnecting a credential
 * refuse with "in use by N tracked places", and this is where a user finds out
 * WHICH N.
 */
export default function ProviderCatalog({
  orgId,
  connections,
  onConnect,
  onDisconnect,
  deletingId,
}: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [targets, setTargets] = useState<Record<string, TargetsState>>({});

  const toggle = useCallback(
    async (connectionId: string) => {
      const isOpen = expanded[connectionId];
      setExpanded((prev) => ({ ...prev, [connectionId]: !isOpen }));
      if (isOpen || targets[connectionId]?.targets) return;

      setTargets((prev) => ({
        ...prev,
        [connectionId]: { loading: true, error: false, targets: null },
      }));
      try {
        const rows = await integrationService.listConnectionTargets(orgId, connectionId);
        setTargets((prev) => ({
          ...prev,
          [connectionId]: { loading: false, error: false, targets: rows },
        }));
      } catch {
        // A failed load and "nothing routes through this" are different
        // answers; rendering the empty state for both would hide an error.
        setTargets((prev) => ({
          ...prev,
          [connectionId]: { loading: false, error: true, targets: null },
        }));
      }
    },
    [orgId, expanded, targets]
  );

  return (
    <Stack spacing={2}>
      {PROVIDERS.map((provider) => {
        const mine = connections.filter((c) => c.provider === provider.id);
        const isConnected = mine.length > 0;

        return (
          <Paper
            key={provider.id}
            variant="outlined"
            sx={{ p: 3 }}
            component="section"
            aria-label={provider.label}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1.5} alignItems="center">
                {provider.id === 'github' ? <GitHubIcon /> : <HubIcon />}
                <Box>
                  <Typography variant="h6" component="h2">
                    {provider.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {provider.blurb}
                  </Typography>
                </Box>
              </Stack>

              <Stack direction="row" spacing={1} alignItems="center">
                {!provider.available ? (
                  // REV-695: the reason sits next to the chip as VISIBLE text.
                  // A title attribute or Tooltip would have hidden it behind a
                  // hover and is unreliably exposed to assistive tech - not a
                  // defensible choice in this product, and it would answer the
                  // ticket's question only for sighted mouse users.
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      size="small"
                      label={t('integrations.notAvailable', 'Not yet available')}
                      variant="outlined"
                    />
                    {provider.unavailableReason && (
                      <Typography variant="body2" color="text.secondary">
                        {provider.unavailableReason}
                      </Typography>
                    )}
                  </Stack>
                ) : isConnected ? (
                  <Chip
                    size="small"
                    color="success"
                    variant="outlined"
                    label={t('integrations.connectedCount', {
                      defaultValue: '{{count}} connected',
                      count: mine.length,
                    })}
                  />
                ) : (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={t('integrations.notConnected', 'Not connected')}
                  />
                )}
                {provider.available && (
                  <Button variant="contained" size="small" onClick={() => onConnect(provider.id)}>
                    {t('integrations.connectProvider', {
                      defaultValue: 'Connect {{provider}}',
                      provider: provider.label,
                    })}
                  </Button>
                )}
              </Stack>
            </Stack>

            {isConnected && (
              <List sx={{ mt: 1 }}>
                {mine.map((c) => {
                  const state = targets[c.id];
                  // REV-514 allows an auth-only connection with no repo.
                  // Assuming config is present yields "undefined/undefined".
                  const label = c.config ? `${c.config.owner}/${c.config.repo}` : provider.label;
                  return (
                    // A <ul> may only contain <li>. Wrapping the row and its
                    // disclosure in a Box put a <div> between them, which our
                    // own engine flagged — ListItem is component="div" here so
                    // this <li> is the only list item.
                    <li key={c.id} style={{ listStyle: 'none' }}>
                      <ListItem
                        component="div"
                        disableGutters
                        secondaryAction={
                          <IconButton
                            edge="end"
                            aria-label={t('integrations.remove', 'Remove connection')}
                            onClick={() => onDisconnect(c.id)}
                            disabled={deletingId === c.id}
                          >
                            {deletingId === c.id ? <CircularProgress size={20} /> : <DeleteIcon />}
                          </IconButton>
                        }
                      >
                        <Button
                          onClick={() => toggle(c.id)}
                          startIcon={
                            <ExpandMoreIcon
                              sx={{
                                transform: expanded[c.id] ? 'rotate(180deg)' : 'none',
                                transition: 'transform 150ms',
                              }}
                            />
                          }
                          aria-expanded={Boolean(expanded[c.id])}
                          sx={{ textTransform: 'none' }}
                        >
                          {label}
                        </Button>
                      </ListItem>

                      {/* Rendered conditionally rather than with <Collapse>:
                          a collapsed Collapse keeps a fixed 0px height with
                          overflow:hidden, which the engine reports as text cut
                          off vertically (CSS-06). Losing the animation is the
                          right trade for a product that sells this check. */}
                      {expanded[c.id] && (
                        <Box sx={{ pl: 4, pb: 2 }}>
                          {state?.loading && <CircularProgress size={20} />}
                          {state?.error && (
                            <Alert severity="error">
                              {t(
                                'integrations.targetsFailed',
                                'Could not load the tracked places using this connection.'
                              )}
                            </Alert>
                          )}
                          {state?.targets && state.targets.length === 0 && (
                            <Typography variant="body2" color="text.secondary">
                              {t(
                                'integrations.noTargets',
                                'No tracked places export to this connection yet.'
                              )}
                            </Typography>
                          )}
                          {state?.targets && state.targets.length > 0 && (
                            <List dense>
                              {state.targets.map((tg) => (
                                <ListItem key={tg.id} disableGutters>
                                  <ListItemText
                                    primary={tg.context?.name ?? tg.context_id}
                                    secondary={
                                      tg.config ? `${tg.config.owner}/${tg.config.repo}` : undefined
                                    }
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      )}
                    </li>
                  );
                })}
              </List>
            )}
          </Paper>
        );
      })}
    </Stack>
  );
}
